import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { Attachment } from '../models/Content';
import { getPreviewType } from '../utils/fileUtils';

interface FilePreviewModalProps {
  attachment: Attachment;
  isOpen: boolean;
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  attachment,
  isOpen,
  onClose
}) => {
  const [pdfError, setPdfError] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  if (!isOpen) return null;

  const previewType = getPreviewType(attachment.type);

  // Load PDF.js dynamically
  useEffect(() => {
    if (previewType === 'pdf' && isOpen) {
      loadPdfJs();
    }
  }, [previewType, isOpen]);

  const loadPdfJs = async () => {
    try {
      setIsLoading(true);
      
      // Load PDF.js from CDN
      if (!(window as any).pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          loadPdf();
        };
        script.onerror = () => {
          console.error('Failed to load PDF.js');
          setPdfError(true);
          setIsLoading(false);
        };
        document.head.appendChild(script);
      } else {
        loadPdf();
      }
    } catch (error) {
      console.error('Error loading PDF.js:', error);
      setPdfError(true);
      setIsLoading(false);
    }
  };

  const loadPdf = async () => {
    try {
      const pdfjsLib = (window as any).pdfjsLib;
      const loadingTask = pdfjsLib.getDocument({
        url: attachment.url,
        httpHeaders: {},
        withCredentials: false
      });
      
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      renderPage(pdf, 1);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setPdfError(true);
      setIsLoading(false);
    }
  };

  const renderPage = async (pdf: any, pageNum: number) => {
    if (!canvasRef.current) return;
    
    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale, rotation });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
    } catch (error) {
      console.error('Error rendering page:', error);
      setPdfError(true);
    }
  };

  // Re-render page when scale, rotation, or page changes
  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(pdfDoc, currentPage);
    }
  }, [pdfDoc, currentPage, scale, rotation]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const openInNewTab = () => {
    window.open(attachment.url, '_blank');
  };

  const renderPreviewContent = () => {
    switch (previewType) {
      case 'image':
        return (
          <img
            src={attachment.url}
            alt={attachment.name}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              console.error('Failed to load image:', e);
              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzlDQTNBRiI+SW1hZ2UgZmFpbGVkIHRvIGxvYWQ8L3RleHQ+PC9zdmc+';
            }}
          />
        );

      case 'pdf':
        if (pdfError) {
          return (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-center text-gray-700 mb-6">
                <div className="text-lg mb-2">PDF Preview Not Available</div>
                <div className="text-sm text-gray-500 mb-4">
                  Unable to load PDF for preview.
                </div>
              </div>
              <button
                onClick={openInNewTab}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open PDF in New Tab
              </button>
            </div>
          );
        }

        if (isLoading) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <div className="text-lg text-gray-700">Loading PDF...</div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="w-full h-full flex flex-col">
            {/* PDF Controls */}
            {pdfDoc && (
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleZoomOut}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                    title="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600 min-w-[60px] text-center">
                    {Math.round(scale * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                    title="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRotate}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                    title="Rotate"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={openInNewTab}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            {/* PDF Canvas */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              <div className="flex items-center justify-center min-h-full">
                <canvas
                  ref={canvasRef}
                  className="border border-gray-300 bg-white shadow-lg"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <iframe
            src={attachment.url}
            className="w-full h-full border-0 bg-white"
            title={attachment.name}
          />
        );

      case 'video':
        return (
          <video
            src={attachment.url}
            controls
            className="max-w-full max-h-full"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        );

      case 'audio':
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="mb-4 text-lg font-medium text-gray-900">
              {attachment.name}
            </div>
            <audio
              src={attachment.url}
              controls
              className="w-full max-w-md"
              preload="metadata"
            >
              Your browser does not support the audio tag.
            </audio>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <div className="text-lg mb-2">Preview not available</div>
              <div className="text-sm">File type: {attachment.type}</div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl max-w-7xl max-h-[95vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900 truncate">
              {attachment.name}
            </h3>
            <p className="text-sm text-gray-500">
              {attachment.type} • {Math.round(attachment.size / 1024)} KB
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            title="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(95vh - 80px)' }}>
          {renderPreviewContent()}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal; 